<template>
  <v-container fluid v-if="location">
    <!-- Title + Actions -->
    <v-card class="mb-5 rounded-xl elevation-2">
      <v-card-title class="d-flex justify-space-between align-center">
        <div>
          <h2 class="font-weight-bold mb-1">{{ location.name }}</h2>
          <span class="text-caption grey--text">Code: {{ location.code }}</span>

          <!-- 🔹 Business Type Selector -->
          <div class="mt-2">
            <v-select
              v-model="location.businessType"
              :items="businessTypes"
              label="Business Type"
              dense
              outlined
              hide-details
              class="mt-1"
              style="max-width: 200px"
              @change="updateBusinessType"
            />
          </div>
        </div>

        <v-btn
          color="subCard"
          rounded
          :href="`${terminalURL}/login/location?code=${location.code}`"
          target="_blank"
        >
          <v-icon left small>mdi-open-in-new</v-icon>
          Open Terminal
        </v-btn>
      </v-card-title>
    </v-card>

    <!-- Info & Date Filter -->
    <v-row>
      <v-col cols="12" md="6">
        <v-card outlined class="pa-4">
          <h3 class="subtitle-1 font-weight-bold mb-2">Address</h3>
          <p class="mb-1">{{ location.Address.addressLine1 }}</p>
          <p class="mb-1" v-if="location.Address.addressLine2">
            {{ location.Address.addressLine2 }}
          </p>
          <p class="mb-1">
            {{ location.Address.city }}, {{ location.Address.State.name }}
            {{ location.Address.postalCode }}
          </p>
          <p>
            <v-icon small class="mr-1 grey--text">mdi-phone</v-icon>
            {{ location.Address.phoneNumber }}
          </p>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card outlined class="pa-4">
          <DateTimePicker
            :defaultRange="dateTime.range"
            :range="true"
            v-model="dateTime"
            :initStartDate="start"
            :initEndDate="end"
            @submit="getLocationSales()"
          />
          <div class="mt-3">
            <p class="mb-1">
              <strong>From:</strong>
              {{ dateTime.start | moment("LLL") }}
            </p>
            <p>
              <strong>To:</strong>
              {{ dateTime.end | moment("LLL") }}
            </p>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Sales -->
    <v-card outlined class="mt-5">
      <v-card-title class="font-weight-bold">Sales Overview</v-card-title>
      <v-card-text>
        <SaleBaseTile
          ref="saleBaseTile"
          :dateTime="dateTime"
          :id="location.id"
        />
      </v-card-text>
    </v-card>

    <!-- Expansion Panels -->
    <v-expansion-panels class="mt-5" accordion>
      <!-- Subscription -->
      <v-expansion-panel>
        <v-expansion-panel-header>
          <v-row no-gutters align="center">
            <v-icon left small color="primary" class="mr-2"
              >mdi-credit-card</v-icon
            >
            <span>Subscription</span>
          </v-row>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <Subscription :location="location" />
        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- Employees -->
      <v-expansion-panel>
        <v-expansion-panel-header>
          <v-row no-gutters align="center">
            <v-icon left small color="blue" class="mr-2"
              >mdi-account-group</v-icon
            >
            <span>Employees</span>
          </v-row>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <EmployeeList :LocationId="location.id" hideHeader hideCreate />
        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- Settings -->
      <v-expansion-panel>
        <v-expansion-panel-header>
          <v-row no-gutters align="center">
            <v-icon left small color="grey" class="mr-2">mdi-cog</v-icon>
            <span>Settings</span>
          </v-row>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <Settings :LocationId="location.id" />
        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- Notifications -->
      <v-expansion-panel>
        <v-expansion-panel-header>
          <v-row no-gutters align="center">
            <v-icon left small color="orange" class="mr-2">mdi-bell</v-icon>
            <span>Notifications</span>
          </v-row>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <Notifications :LocationId="location.id" :location="location" />
        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- Reports -->
      <v-expansion-panel>
        <v-expansion-panel-header>
          <v-row no-gutters align="center">
            <v-icon left small color="pink" class="mr-2">mdi-file-chart</v-icon>
            <span>Reports</span>
          </v-row>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <Reports
            :LocationId="location.id"
            :location="location"
            :Time="dateTime"
          />
        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- Expense -->
      <v-expansion-panel v-if="getLocationIntegration">
        <v-expansion-panel-header>
          <v-row no-gutters align="center">
            <v-icon left small color="red" class="mr-2">mdi-cash-minus</v-icon>
            <span>Expense</span>
          </v-row>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <Expense
            :locationIntegration="locationIntegration"
            :LocationId="location.id"
            :location="location"
          />
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-container>
</template>
<script>
import { mapGetters } from "vuex";
import locationService from "@/modules/Location/service.js";
import DateTimePicker from "@/components/DateTimePicker";
import SaleBaseTile from "@/modules/Location/components/SaleBase.vue";
// import UpdatePaymentButton from "../components/UpdatePayment";
// import UpdatePlan from "../components/UpdatePlan";
import EmployeeList from "../../Employee/components/List";
import Settings from "../components/Settings";
import Reports from "../components/Reports";
import Expense from "../components/Expense";
import Notifications from "../components/Notifications";
import Subscription from "@/modules/Location/components/Subscription.vue";

export default {
  name: "location-view-profile",
  components: {
    // UpdatePaymentButton,
    // UpdatePlan,
    EmployeeList,
    Settings,
    Reports,
    Subscription,
    SaleBaseTile,
    Notifications,
    Expense,
    DateTimePicker,
  },
  data() {
    return {
      businessTypes: [
        { text: "Retail Store", value: "retail" },
        { text: "Wireless Store", value: "wireless" },
        { text: "Auto Repair", value: "auto" },
      ],
      start: this.$moment().startOf("day"),
      end: this.$moment().endOf("day"),
      dateTime: {
        range: "thisMonth",
        start: this.$moment().startOf("day").toISOString(),
        end: this.$moment().endOf("day").toISOString(),
      },
      terminalURL: process.env.VUE_APP_TERMINALURL,
      location: null,
      Reports: {
        Sales: null,
      },
      subscription: null,
      isLoading: true,
      locationIntegration: false,
    };
  },
  async created() {
    await this.loadLocation();
    this.getTerminalExpense();
    this.$Progress.start();
    this.getLocationSales();

    // this.loadSubscription();
  },
  computed: {
    getLocationIntegration() {
      return this.locationIntegration;
    },
    cashInHand() {
      return this.Reports.Sales.cash.total - this.Reports.Sales.Expense.total;
    },
    startDateFormatted() {
      if (this.startDatePicker.date) {
        // console.log(this.startDatePicker.date);
        return this.$moment(this.startDatePicker.date).format("MM/DD/YYYY");
      }
      return null;
    },
    endDateFormatted: {
      get() {
        if (this.endDatePicker.date) {
          return this.$moment(this.endDatePicker.date).format("MM/DD/YYYY");
        }
        return null;
      },
      set() {
        return (this.endDatePicker.date = null);
      },
    },
    ...mapGetters("integration", ["locationModules"]),
  },
  methods: {
    async updateBusinessType() {
      if (!this.location || !this.location.id) return;

      const payload = {
        businessType: this.location.businessType,
      };

      try {
        await locationService.update(this.location.id, payload);
        this.$toasted.show("Business type updated successfully!", {
          type: "success",
          duration: 2000,
        });
      } catch (error) {
        this.$toasted.show("Failed to update business type.", {
          type: "error",
          duration: 2500,
        });
        console.error("Error updating business type:", error);
      }
    },
    getLocationSales() {
      this.isLoading = true;
      this.$Progress.start();
      locationService.Reports.getLocationSales({
        start: this.dateTime.start,
        end: this.dateTime.end,
        // today: true,
        LocationId: this.location.id,
      }).then((response) => {
        this.$Progress.finish();
        this.Reports.Sales = response.data.report;
        this.isLoading = false;
      });
    },
    async getTerminalExpense() {
      locationService
        .getIntegrationByLocation(this.location.id, {
          module: "terminalExpense",
        })
        .then((locationIntegration) => {
          if (locationIntegration.data[0]) {
            this.locationIntegration = locationIntegration.data[0];
          }
        });
    },

    loadLocation() {
      if (!this.$route.params || !this.$route.params.id)
        return this.$router.push("/location");
      const { id } = this.$route.params;
      this.$Progress.start();
      return locationService
        .getbyId(id)
        .then((response) => {
          this.$Progress.finish();
          this.isLoading = false;
          this.location = response.data;
        })
        .catch(() => {
          this.$router.push("/location");
        });
    },
    // loadSubscription() {
    //   return locationService.Billing.getSubscription(this.location.id)
    //     .then((response) => {
    //       console.log("sub", response.data);
    //       this.subscription = response.data;
    //     })
    //     .catch((error) => {
    //       console.log(error);
    //     });
    // },
  },
};
</script>
